import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../components/AuthContext';
import { canEditVendor, canDeactivateVendor, canLockVendor, canTogglePreferredVendor, canEditVendorPrice, canAddVendor, canActionVendorCatalog, canReviewVendor, canExportVendorCatalog, canImportVendorCatalog } from '../../lib/permissions';
import { checkVendorSimilarity } from '../../lib/vendorDedup';
import VendorCatalogExportSheet from '../../components/inventory/VendorCatalogExportSheet';
import MasterDataImportSheet from '../../components/inventory/MasterDataImportSheet';
import VendorOwnCatalogSection from '../../components/inventory/VendorOwnCatalogSection';
import Overlay from '../../components/Overlay';
import { useTier, usesPhoneLayout } from '../../lib/tiers';
import UnitSelect from '../../components/UnitSelect';
import {
  IconSearch,
  IconPlus,
  IconStar,
  IconLock,
  IconPhone,
  IconMail,
  IconEdit,
  IconCheck,
  IconX,
  IconBuildingStore,
  IconChevronLeft,
  IconAlertTriangle,
  IconInfoCircle,
} from '@tabler/icons-react';

// ── CONSTANTS ─────────────────────────────────────────────────────────────────

const STATUS_FILTERS = ['All', 'Active', 'Inactive'];

// ── HELPERS ───────────────────────────────────────────────────────────────────

function fmtPrice(price, uom) {
  if (price == null) return null;
  return `$${parseFloat(price).toFixed(2)}${uom ? '/' + uom : ''}`;
}

function fmtDate(ts) {
  if (!ts) return null;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── PRICE HISTORY PAGE ────────────────────────────────────────────────────────

const FIELD_LABELS = {
  vendor_price: 'Vendor price',
  pack_qty: 'Pack qty',
  pack_unit: 'Pack unit',
  vendor_item_number: 'Vendor item #',
  unit_cost: 'Unit cost',
  is_preferred: 'Preferred',
  price_locked: 'Locked',
  associated: 'Associated',
  removed: 'Removed',
};

const FIELD_FILTERS = ['All', 'vendor_price', 'pack_qty', 'pack_unit', 'vendor_item_number', 'is_preferred', 'price_locked', 'associated', 'removed'];

function PriceHistoryPage({ onBack, filterVendorId, filterVendorName, isMobile }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fieldFilter, setFieldFilter] = useState('All');

  useEffect(() => {
    async function load() {
      setLoading(true);
      const since = new Date();
      since.setDate(since.getDate() - 90);

      let query = supabase
        .from('vendor_item_audit_log')
        .select(`
          id, field_changed, old_value, new_value, changed_at,
          vendor_id, item_id, vendor_name_snapshot, item_name_snapshot,
          users ( id, full_name )
        `)
        .gte('changed_at', since.toISOString())
        .order('changed_at', { ascending: false })
        .limit(500);

      if (filterVendorId) {
        query = query.eq('vendor_id', filterVendorId);
      }

      const { data, error } = await query;
      if (!error) setRows(data || []);
      setLoading(false);
    }
    load();
  }, [filterVendorId]);

  const filtered = rows.filter(r => {
    if (fieldFilter !== 'All' && r.field_changed !== fieldFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [
        r.item_name_snapshot,
        r.vendor_name_snapshot,
        r.users?.full_name,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  function fmtVal(field, val) {
    if (val == null || val === '') return '—';
    if (field === 'vendor_price' || field === 'unit_cost') return `$${parseFloat(val).toFixed(2)}`;
    if (field === 'is_preferred' || field === 'price_locked') return val === 'true' ? 'Yes' : 'No';
    return String(val);
  }

  function fmtTs(ts) {
    if (!ts) return '—';
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function eventStyle(field) {
    if (field === 'removed') return { bg: '#FFEBEE', color: '#A32D2D' };
    if (field === 'associated') return { bg: '#E6F1FB', color: '#0C447C' };
    if (field === 'price_locked') return { bg: '#EEEDFE', color: '#3C3489' };
    return { bg: '#E1F5EE', color: '#085041' };
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid #E8E8E8', background: '#FAFAF9', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
          <button
            onClick={onBack}
            style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#1D9E75', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}
          >
            <IconChevronLeft size={14} /> Back to vendors
          </button>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0F0F0F' }}>
            Price history {filterVendorName ? `— ${filterVendorName}` : '(all vendors)'}
          </div>
          <div style={{ fontSize: 11, color: '#9A9A9A', marginLeft: 'auto' }}>Last 90 days</div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #E0E0E0', borderRadius: 8, padding: '6px 10px', background: 'white', flex: 1, minWidth: 180 }}>
            <IconSearch size={14} color="#9A9A9A" />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search item, vendor, user..."
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {FIELD_FILTERS.map(f => (
              <button key={f} onClick={() => setFieldFilter(f)}
                style={{
                  padding: '4px 10px', borderRadius: 20,
                  border: `1px solid ${fieldFilter === f ? '#1D9E75' : '#E0E0E0'}`,
                  background: fieldFilter === f ? '#E1F5EE' : 'white',
                  color: fieldFilter === f ? '#085041' : '#7A7A7A',
                  fontSize: 11, fontWeight: fieldFilter === f ? 700 : 400, cursor: 'pointer',
                }}
              >
                {f === 'All' ? 'All' : (FIELD_LABELS[f] || f)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9A9A9A', fontSize: 13 }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9A9A9A', fontSize: 13 }}>No activity found.</div>
        ) : isMobile ? (
          <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map(r => {
              const es = eventStyle(r.field_changed);
              return (
                <div key={r.id} style={{ background: 'white', border: '1px solid #E8E8E8', borderRadius: 10, padding: '10px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.item_name_snapshot || '—'}
                    </div>
                    <span style={{ background: es.bg, color: es.color, borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                      {FIELD_LABELS[r.field_changed] || r.field_changed}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: '#7A7A7A', marginBottom: 4 }}>{r.vendor_name_snapshot || '—'}</div>
                  {(r.old_value != null || r.new_value != null) && (
                    <div style={{ fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: '#9A9A9A', textDecoration: 'line-through' }}>{fmtVal(r.field_changed, r.old_value)}</span>
                      <span style={{ color: '#9A9A9A' }}> to </span>
                      <span style={{ color: '#1D9E75', fontWeight: 600 }}>{fmtVal(r.field_changed, r.new_value)}</span>
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: '#9A9A9A' }}>{fmtTs(r.changed_at)} &middot; {r.users?.full_name || '—'}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#FAFAF9', borderBottom: '1px solid #E8E8E8' }}>
                {['Date', 'Vendor', 'Item', 'Event', 'Old value', 'New value', 'Changed by'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '.5px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const es = eventStyle(r.field_changed);
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid #F0F0F0' }}>
                    <td style={{ padding: '8px 12px', color: '#5C5C5C', whiteSpace: 'nowrap' }}>{fmtTs(r.changed_at)}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 600, color: '#0F0F0F' }}>{r.vendor_name_snapshot || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#0F0F0F' }}>{r.item_name_snapshot || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ background: es.bg, color: es.color, borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>
                        {FIELD_LABELS[r.field_changed] || r.field_changed}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', color: '#9A9A9A', textDecoration: 'line-through' }}>{fmtVal(r.field_changed, r.old_value)}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1D9E75' }}>{fmtVal(r.field_changed, r.new_value)}</td>
                    <td style={{ padding: '8px 12px', color: '#5C5C5C' }}>{r.users?.full_name || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── STAT BAR ──────────────────────────────────────────────────────────────────

function StatBar({ vendors, vendorItems, onPriceHistory, canReview, onNeedsReviewClick, canExport, onExport, canImport, onImport }) {
  const activeCount = vendors.filter(v => v.is_active).length;
  const needsReviewCount = vendors.filter(v => !v.reviewed_at).length;
  const contractCount = vendorItems.filter(vi => vi.price_locked).length;
  // #422, Session 78s.1. LINKED rows only. This tile is labelled "Items
  // supplied" and until now every vendor_items row was one, because the only
  // rows without an item came from the occasional off-catalog order. Once a
  // supplier's whole price list can be imported in one press, most rows are
  // products D&R does not stock — and counting those here would report four
  // hundred items supplied by a vendor who supplies nine.
  const itemCount = vendorItems.filter(vi => vi.item_id != null).length;

  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const recentPrices = vendorItems.filter(vi =>
    vi.price_date && vi.price_date >= monthStart && vi.vendor_price
  );
  const spendApprox = recentPrices.reduce((s, vi) => s + parseFloat(vi.vendor_price || 0), 0);

  const stats = [
    { label: 'Active vendors', value: activeCount, color: '#0F0F0F' },
    ...(canReview ? [{ label: 'Needs review', value: needsReviewCount, color: needsReviewCount > 0 ? '#633806' : '#0F0F0F', highlight: needsReviewCount > 0, onClick: onNeedsReviewClick }] : []),
    { label: 'Contract locked', value: contractCount, color: '#3C3489' },
    { label: 'Spend this month', value: spendApprox > 0 ? `$${(spendApprox / 1000).toFixed(1)}k` : '—', color: '#9A5F00' },
    { label: 'Items supplied', value: itemCount, color: '#0F0F0F' },
  ];

  return (
    <div style={{
      display: 'flex',
      alignItems: 'stretch',
      borderBottom: '1px solid #E8E8E8',
      background: '#FAFAF9',
      flexShrink: 0,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${stats.length}, 1fr)`, flex: 1 }}>
        {stats.map((s, i) => (
          <div key={s.label} onClick={s.onClick} style={{
            padding: '12px 20px',
            borderRight: '1px solid #E8E8E8',
            textAlign: 'center',
            background: s.highlight ? '#FAEEDA' : 'transparent',
            cursor: s.onClick ? 'pointer' : 'default',
          }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: s.highlight ? '#633806' : '#7A7A7A', marginTop: 1, fontWeight: s.highlight ? 700 : 400 }}>{s.label}</div>
          </div>
        ))}
      </div>
      {/* #194 — the export sits at the TOP of the page beside Price history,
          not at the bottom of the list beside Add vendor. #432's lesson from
          the session before this one: a control below a list longer than a
          screen is a control nobody can reach. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px', flexShrink: 0 }}>
        {canImport && (
          <button
            onClick={onImport}
            style={{
              padding: '7px 14px', background: 'white', color: '#0F0F0F',
              border: '1px solid #E0E0E0', borderRadius: 7,
              fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {/* Session 78u. Was "Import CSV", which went stale the moment the
                workbooks landed - the screen now takes .xlsx as well, and a
                label naming one format tells somebody holding the other that
                they are in the wrong place. */}
            Import
          </button>
        )}
        {canExport && (
          <button
            onClick={onExport}
            style={{
              padding: '7px 14px', background: 'white', color: '#0F0F0F',
              border: '1px solid #E0E0E0', borderRadius: 7,
              fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            Export CSV
          </button>
        )}
        <button
          onClick={onPriceHistory}
          style={{
            padding: '7px 14px', background: 'white', color: '#0F0F0F',
            border: '1px solid #E0E0E0', borderRadius: 7,
            fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          Price history
        </button>
      </div>
    </div>
  );
}

// ── VENDOR CARD (list) ────────────────────────────────────────────────────────

function VendorCard({ vendor, isSelected, onClick, isMobile, canContact }) {
  // Linked rows only — the same correction as the Items supplied tile, and for
  // the same reason. The badge says "N items", meaning items we stock.
  const itemCount = vendor.vendor_items?.filter(vi => vi.item_id != null).length ?? 0;
  const preferredCount = vendor.vendor_items?.filter(vi => vi.is_preferred).length ?? 0;
  const hasContract = vendor.vendor_items?.some(vi => vi.price_locked) ?? false;

  return (
    <div
      onClick={onClick}
      style={{
        background: isSelected ? '#F0FFF8' : 'white',
        border: `1px solid ${isSelected ? '#5DCAA5' : '#E8E8E8'}`,
        borderRadius: 8,
        padding: '10px 12px',
        cursor: 'pointer',
        marginBottom: 6,
        opacity: vendor.is_active ? 1 : 0.6,
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: 2,
        gap: 8,
      }}>
        <div style={{
          fontSize: 13,
          fontWeight: 700,
          color: '#0F0F0F',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {vendor.name}
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {!vendor.reviewed_at && (
            <span style={{
              fontSize: 10, fontWeight: 700,
              background: '#FAEEDA', color: '#633806',
              borderRadius: 4, padding: '2px 7px',
            }}>
              Needs review
            </span>
          )}
          {preferredCount > 0 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              fontSize: 10, fontWeight: 700,
              background: '#E1F5EE', color: '#085041',
              borderRadius: 4, padding: '2px 7px',
            }}>
              <IconStar size={9} />
              Preferred on {preferredCount}
            </span>
          )}
          {hasContract && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              fontSize: 10, fontWeight: 700,
              background: '#FAEEDA', color: '#633806',
              borderRadius: 4, padding: '2px 7px',
            }}>
              <IconLock size={9} />
              Contract
            </span>
          )}
          {itemCount === 0 && (
            <span style={{
              fontSize: 10, fontWeight: 600,
              background: '#F0F0F0', color: '#9A9A9A',
              borderRadius: 4, padding: '2px 7px',
            }}>
              No items
            </span>
          )}
          {!vendor.is_active && (
            <span style={{
              fontSize: 10, fontWeight: 600,
              background: '#F0F0F0', color: '#9A9A9A',
              borderRadius: 4, padding: '2px 7px',
            }}>
              Inactive
            </span>
          )}
        </div>
      </div>

      <div style={{ fontSize: 11, color: '#7A7A7A', marginBottom: 6 }}>
        {[
          vendor.contact_name || null,
          itemCount > 0 ? `${itemCount} item${itemCount !== 1 ? 's' : ''}` : null,
        ].filter(Boolean).join(' · ') || 'No contact on file'}
      </div>

      {canContact && (
      <div style={{ display: 'flex', gap: 6 }}>
        {vendor.phone ? (
          <a
            href={`tel:${vendor.phone}`}
            onClick={e => e.stopPropagation()}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', background: '#1D9E75', color: 'white',
              borderRadius: 6, fontSize: 11, fontWeight: 600, textDecoration: 'none',
            }}
          >
            <IconPhone size={11} />
            Call {vendor.contact_name?.split(' ')[0] || ''}
          </a>
        ) : (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 10px', background: '#F5F5F3', color: '#9A9A9A',
            borderRadius: 6, fontSize: 11, fontStyle: 'italic',
          }}>
            No contact
          </span>
        )}
        {vendor.email && (
          <a
            href={`mailto:${vendor.email}`}
            onClick={e => e.stopPropagation()}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', background: 'white', color: '#0F0F0F',
              border: '1px solid #E0E0E0', borderRadius: 6, fontSize: 11,
              fontWeight: 600, textDecoration: 'none',
            }}
          >
            <IconMail size={11} />
            Email
          </a>
        )}
      </div>
      )}
    </div>
  );
}

// ── ADD VENDOR SHEET ──────────────────────────────────────────────────────────

function AddVendorSheet({ visible, onClose, onSaved, isMobile }) {
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dupWarning, setDupWarning] = useState(null);

  useEffect(() => {
    if (visible) {
      setName(''); setContact(''); setPhone('');
      setEmail(''); setNotes(''); setIsInternal(false);
      setError(''); setDupWarning(null);
    }
  }, [visible]);

  // #211, Session 79.14f. THE WARNING MUST NOT OUTLIVE THE VALUES IT WAS ABOUT.
  //
  // Save works in two presses on purpose: the first runs the similarity check
  // and, if something looks close, shows the warning and stops. The second press
  // SKIPS the check -- `if (!dupWarning)` -- which is what lets a person say
  // "yes, I mean it".
  //
  // The hole: edit the name after the warning appears and the warning is still
  // set, so the next press saves a DIFFERENT vendor that was never checked
  // against anything. The person believes they were warned about what they are
  // saving, and they were warned about something else.
  //
  // Clearing on edit puts the next press back on the first rung. The effect
  // cannot clear the warning it just set, because setting it changes none of
  // these three values.
  useEffect(() => {
    setDupWarning(null);
  }, [name, phone, email]);

  if (!visible) return null;

  async function save() {
    if (!name.trim()) { setError('Vendor name is required.'); return; }
    setError('');

    if (!dupWarning) {
      const { matched, matchedVendor } = await checkVendorSimilarity(name, phone, email);
      if (matched) {
        setDupWarning(matchedVendor);
        return;
      }
    }

    setSaving(true);
    try {
      const { data, error: err } = await supabase
        .from('inventory_vendors')
        .insert({
          name: name.trim(),
          contact_name: contact.trim() || null,
          phone: phone.trim() || null,
          email: email.trim() || null,
          notes: notes.trim() || null,
          is_internal: isInternal,
          is_active: true,
          similarity_warning_note: dupWarning ? `Flagged as similar to "${dupWarning.name}" at creation` : null,
        })
        .select('id, name, contact_name, phone, email, notes, is_internal, is_active')
        .single();
      if (err) throw err;
      onSaved(data);
    } catch (e) {
      setError('Could not save vendor: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  const panelStyle = isMobile ? {
    position: 'fixed',
    bottom: 0, left: 0, right: 0,
    background: 'white',
    borderRadius: '16px 16px 0 0',
    padding: '20px 20px 40px',
    maxHeight: '90vh',
    overflowY: 'auto',
    zIndex: 300,
    boxShadow: '0 -4px 32px rgba(0,0,0,0.14)',
  } : {
    position: 'absolute',
    top: 0, right: 0, bottom: 0,
    width: 360,
    background: 'white',
    borderLeft: '1px solid #E8E8E8',
    padding: '20px',
    overflowY: 'auto',
    zIndex: 300,
    boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 299 }}
      />
      <div style={panelStyle}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, color: '#0F0F0F' }}>
            <IconBuildingStore size={16} />
            Add vendor
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid #E0E0E0', borderRadius: '50%',
              width: 28, height: 28, display: 'flex', alignItems: 'center',
              justifyContent: 'center', cursor: 'pointer', padding: 0,
            }}
          >
            <IconX size={14} color="#7A7A7A" />
          </button>
        </div>

        {error && (
          <div style={{
            background: '#FFEBEE', color: '#A32D2D', borderRadius: 8,
            padding: '8px 12px', fontSize: 12, marginBottom: 12,
          }}>
            {error}
          </div>
        )}

        <FieldLabel>Vendor name *</FieldLabel>
        <input type="text" value={name} onChange={e => setName(e.target.value)}
          placeholder="e.g. Grimco Supply" style={inputStyle} />

        <FieldLabel>Contact name</FieldLabel>
        <input type="text" value={contact} onChange={e => setContact(e.target.value)}
          placeholder="e.g. Mike Torres" style={inputStyle} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
          <div>
            <FieldLabel>Phone</FieldLabel>
            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="800-000-0000" style={{ ...inputStyle, marginBottom: 0 }} />
          </div>
          <div>
            <FieldLabel>Email</FieldLabel>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="orders@vendor.com" style={{ ...inputStyle, marginBottom: 0 }} />
          </div>
        </div>

        <FieldLabel>Notes</FieldLabel>
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="e.g. Net 30. Call before 4pm CST." rows={3}
          style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginBottom: 20 }}>
          <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)}
            style={{ accentColor: '#1D9E75' }} />
          Internal vendor (D&R branch-to-branch)
        </label>

        {dupWarning && (
          <div style={{
            background: '#FFF8E6', border: '1px solid #F0C040', borderRadius: 8,
            padding: '8px 12px', fontSize: 11, color: '#633806', marginBottom: 12,
          }}>
            Similar to existing vendor "{dupWarning.name}" — confirm this is a distinct vendor before saving.
          </div>
        )}

        <button
          onClick={save}
          disabled={saving}
          style={{
            width: '100%', padding: '12px 0',
            background: saving ? '#E0E0E0' : '#1D9E75',
            color: saving ? '#9A9A9A' : 'white',
            border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 700,
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Saving...' : dupWarning ? 'Save anyway →' : 'Save vendor'}
        </button>
      </div>
    </>
  );
}

// ── ASSOCIATE ITEM SHEET ──────────────────────────────────────────────────────

function PriceInput({ value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', height: 30, border: '1px solid #E0E0E0', borderRadius: 6, overflow: 'hidden', width: 80 }}>
      <span style={{
        padding: '0 6px', fontSize: 12, color: '#7A7A7A',
        background: '#F5F5F3', height: '100%', display: 'flex',
        alignItems: 'center', borderRight: '1px solid #E0E0E0',
      }}>$</span>
      <input
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="0.00"
        style={{
          flex: 1, border: 'none', outline: 'none',
          padding: '0 6px', width: 0,
        }}
      />
    </div>
  );
}

function AssociateItemSheet({ visible, onClose, onSaved, vendorId, existingItemIds, allVendorItems, isMobile }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [queue, setQueue] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const searchTimer = useRef(null);

  useEffect(() => {
    if (visible) {
      setSearch('');
      setResults([]);
      setQueue([]);
      setSaving(false);
      setError('');
    }
  }, [visible]);

  useEffect(() => {
    if (!search.trim()) { setResults([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      const q = search.trim().toLowerCase();
      const { data } = await supabase
        .from('inventory_items')
        .select('id, name, sku, uom')
        .eq('is_active', true)
        .or(`name.ilike.%${q}%,sku.ilike.%${q}%`)
        .limit(20);
      const filtered = (data || []).filter(item =>
        !existingItemIds.includes(item.id) &&
        !queue.some(qi => qi.itemId === item.id)
      );
      setResults(filtered);
    }, 250);
    return () => clearTimeout(searchTimer.current);
  }, [search, existingItemIds, queue]);

  function addToQueue(item) {
    setQueue(prev => [...prev, {
      itemId: item.id,
      itemName: item.name,
      itemUom: item.uom,
      vendorItemNumber: '',
      lastKnownPrice: '',
      isPreferred: false,
    }]);
    setResults(prev => prev.filter(r => r.id !== item.id));
    setSearch('');
  }

  function removeFromQueue(itemId) {
    setQueue(prev => prev.filter(q => q.itemId !== itemId));
  }

  function updateQueue(itemId, field, value) {
    setQueue(prev => prev.map(q => q.itemId === itemId ? { ...q, [field]: value } : q));
  }

  async function handleConfirm() {
    if (queue.length === 0) return;
    setSaving(true);
    setError('');
    try {
      const rows = queue.map(qi => ({
        vendor_id: vendorId,
        item_id: qi.itemId,
        vendor_item_number: qi.vendorItemNumber.trim() || null,
        vendor_price: qi.lastKnownPrice ? parseFloat(qi.lastKnownPrice) : null,
        price_date: qi.lastKnownPrice ? new Date().toISOString() : null,
        is_preferred: qi.isPreferred,
        price_locked: false,
      }));
      const { error: err } = await supabase.from('vendor_items').insert(rows);
      if (err) throw err;
      onSaved();
    } catch (e) {
      setError('Could not save: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  if (!visible) return null;

  const getPreferredVendorForItem = (itemId) => {
    const preferred = allVendorItems.find(vi => vi.item_id === itemId && vi.is_preferred);
    return preferred || null;
  };

  const sheetStyle = isMobile ? {
    position: 'fixed',
    bottom: 0, left: 0, right: 0,
    background: 'white',
    borderRadius: '16px 16px 0 0',
    maxHeight: '92vh',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 300,
    boxShadow: '0 -4px 32px rgba(0,0,0,0.18)',
  } : {
    position: 'absolute',
    top: 0, right: 0, bottom: 0,
    width: 340,
    background: 'white',
    borderLeft: '2px solid #1D9E75',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 300,
    boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 299 }}
      />
      <div style={sheetStyle}>

        {/* Handle (mobile only) */}
        {isMobile && (
          <div style={{ width: 32, height: 4, background: '#D0D0D0', borderRadius: 2, margin: '10px auto 0' }} />
        )}

        {/* Header */}
        <div style={{
          padding: '14px 18px 12px',
          borderBottom: '1px solid #E8E8E8',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0F0F0F' }}>Associate items</div>
              <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 2 }}>
                Select items, fill optional fields, confirm all at once
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none', border: '1px solid #E0E0E0', borderRadius: '50%',
                width: 28, height: 28, display: 'flex', alignItems: 'center',
                justifyContent: 'center', cursor: 'pointer', padding: 0, flexShrink: 0,
              }}
            >
              <IconX size={14} color="#7A7A7A" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div style={{ padding: '12px 18px 0', flexShrink: 0 }}>
          {error && (
            <div style={{
              background: '#FFEBEE', color: '#A32D2D', borderRadius: 8,
              padding: '8px 12px', fontSize: 12, marginBottom: 10,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <IconAlertTriangle size={13} />
              {error}
            </div>
          )}
          <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 5 }}>
            Search items
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            border: '1px solid #E0E0E0', borderRadius: 8,
            padding: '0 10px', height: 36, background: 'white', marginBottom: 8,
          }}>
            <IconSearch size={14} color="#9A9A9A" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Name or SKU…"
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent' }}
              autoFocus
            />
            {search && (
              <button onClick={() => { setSearch(''); setResults([]); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <IconX size={12} color="#9A9A9A" />
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div style={{ padding: '0 18px 8px', flexShrink: 0, maxHeight: 180, overflowY: 'auto' }}>
            {results.map(item => (
              <div
                key={item.id}
                onClick={() => addToQueue(item)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 10px', borderRadius: 8,
                  border: '0.5px solid #E8E8E8', marginBottom: 5,
                  cursor: 'pointer', background: 'white',
                }}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: 4,
                  border: '1.5px solid #D0D0D0', flexShrink: 0,
                }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0F0F0F' }}>{item.name}</div>
                  <div style={{ fontSize: 11, color: '#9A9A9A' }}>
                    {[item.sku, item.uom].filter(Boolean).join(' · ')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {search.trim() && results.length === 0 && (
          <div style={{ padding: '0 18px 8px', fontSize: 12, color: '#9A9A9A', fontStyle: 'italic' }}>
            No matching items found.
          </div>
        )}

        {/* Queue */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 18px' }}>
          {queue.length > 0 && (
            <>
              <div style={{
                fontSize: 10, fontWeight: 700, color: '#7A7A7A',
                textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8,
              }}>
                Queue · {queue.length} item{queue.length !== 1 ? 's' : ''}
              </div>

              {queue.map(qi => {
                const currentPreferred = getPreferredVendorForItem(qi.itemId);
                const hasExistingPreferred = currentPreferred && currentPreferred.vendor_id !== vendorId;
                const noPreferredAnywhere = !currentPreferred;
                const showAutoPref = (noPreferredAnywhere || hasExistingPreferred);
                return (
                  <div key={qi.itemId} style={{ marginBottom: 12 }}>
                    {showAutoPref && (
                      <div style={{
                        background: '#FFF8E6',
                        border: '0.5px solid #F0C040',
                        borderRadius: 6,
                        padding: '6px 10px',
                        fontSize: 11,
                        color: '#633806',
                        marginBottom: 6,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 6,
                      }}>
                        <IconInfoCircle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                        <span>
                          {hasExistingPreferred
                            ? `${currentPreferred.inventory_vendors?.name || 'Another vendor'} is currently preferred for this item — enabling preferred here will replace it.`
                            : 'No preferred vendor set for this item yet — enable preferred to set this vendor as the default.'
                          }
                        </span>
                      </div>
                    )}

                    <div style={{
                      background: '#F8F8F8',
                      borderRadius: 8,
                      border: '0.5px solid #E8E8E8',
                      padding: '10px 12px',
                    }}>
                      {/* Item name row */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#0F0F0F', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {qi.itemName}
                        </div>
                        <button
                          onClick={() => removeFromQueue(qi.itemId)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A9A9A', padding: '0 0 0 8px', flexShrink: 0 }}
                        >
                          <IconX size={14} />
                        </button>
                      </div>

                      {/* Optional fields */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'end', marginBottom: 8 }}>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>
                            Vendor item # <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
                          </div>
                          <input
                            type="text"
                            value={qi.vendorItemNumber}
                            onChange={e => updateQueue(qi.itemId, 'vendorItemNumber', e.target.value)}
                            placeholder="e.g. 3M-GW-24"
                            style={{
                              width: '100%', height: 30, border: '1px solid #E0E0E0',
                              borderRadius: 6, padding: '0 8px',
                              color: '#0F0F0F', background: 'white', boxSizing: 'border-box',
                            }}
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>
                            Last price <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
                          </div>
                          <PriceInput
                            value={qi.lastKnownPrice}
                            onChange={v => updateQueue(qi.itemId, 'lastKnownPrice', v)}
                          />
                        </div>
                      </div>

                      {/* Preferred toggle */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontSize: 12, color: '#0F0F0F' }}>Set as preferred vendor</div>
                          <div style={{ fontSize: 10, color: '#9A9A9A' }}>Pre-fills in Mark Ordered</div>
                        </div>
                        <button
                          onClick={() => updateQueue(qi.itemId, 'isPreferred', !qi.isPreferred)}
                          style={{
                            width: 36, height: 20, borderRadius: 10,
                            background: qi.isPreferred ? '#1D9E75' : '#D0D0D0',
                            border: 'none', cursor: 'pointer', padding: '0 3px',
                            display: 'flex', alignItems: 'center',
                            justifyContent: qi.isPreferred ? 'flex-end' : 'flex-start',
                            transition: 'background 0.15s',
                            flexShrink: 0,
                          }}
                        >
                          <div style={{ width: 14, height: 14, background: 'white', borderRadius: '50%' }} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {queue.length === 0 && !search.trim() && (
            <div style={{ textAlign: 'center', padding: '24px 0', fontSize: 12, color: '#9A9A9A', fontStyle: 'italic' }}>
              Search for items above to add them to the queue.
            </div>
          )}
        </div>

        {/* Footer — confirm */}
        <div style={{
          padding: '12px 18px 20px',
          borderTop: '1px solid #E8E8E8',
          flexShrink: 0,
        }}>
          <button
            onClick={handleConfirm}
            disabled={queue.length === 0 || saving}
            style={{
              width: '100%', height: 40,
              background: queue.length === 0 ? '#E0E0E0' : '#0F0F0F',
              color: queue.length === 0 ? '#9A9A9A' : 'white',
              border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
              cursor: queue.length === 0 || saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            <IconPlus size={15} />
            {saving ? 'Saving…' : queue.length === 0 ? 'Add items to vendor' : `Add ${queue.length} item${queue.length !== 1 ? 's' : ''} to vendor`}
          </button>
          <button
            onClick={onClose}
            style={{
              width: '100%', background: 'none', border: 'none',
              fontSize: 12, color: '#9A9A9A', cursor: 'pointer', padding: '8px 0 0',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}

// ── MOBILE VENDOR ITEM CARD ───────────────────────────────────────────────────

function MobileVendorItemCard({ vi, onTapPrice, onTapItemNum, onTogglePreferred, onRemove, canEditPrice, canTogglePreferred, canRemove, saving }) {
  const itemName = vi.inventory_items?.name || '—';
  const itemUom = vi.inventory_items?.uom || '';
  const isPreferred = vi.is_preferred;
  const isLocked = vi.price_locked;
  const hasPrice = vi.vendor_price != null;
  const hasItemNum = !!vi.vendor_item_number;

  const unitCostDisplay = vi.unit_cost != null
    ? `$${parseFloat(vi.unit_cost).toFixed(2)}${itemUom ? '/' + itemUom : ''}`
    : null;

  const packContextDisplay = hasPrice && vi.pack_qty && vi.pack_unit
    ? `$${parseFloat(vi.vendor_price).toFixed(2)}/${vi.pack_unit} · ${vi.pack_qty} ${itemUom || 'units'}`
    : null;

  return (
    <div style={{
      background: isPreferred ? '#F0FFF8' : 'white',
      border: `1px solid ${isPreferred ? '#5DCAA5' : '#E8E8E8'}`,
      borderRadius: 10, padding: '10px 12px', marginBottom: 8,
    }}>
      {/* Item name + preferred badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {itemName}
        </div>
        {isPreferred && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            fontSize: 10, fontWeight: 700, background: '#E1F5EE', color: '#085041',
            borderRadius: 4, padding: '2px 7px', flexShrink: 0, marginLeft: 8,
          }}>
            <IconStar size={9} /> Preferred
          </span>
        )}
      </div>

      {/* Two tappable field cells */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        {/* Vendor item # cell */}
        <button
          onClick={() => canEditPrice && !isLocked ? onTapItemNum() : null}
          style={{
            background: '#F8F8F8', border: '1px solid #E8E8E8', borderRadius: 8,
            padding: '8px 10px', textAlign: 'left', cursor: canEditPrice && !isLocked ? 'pointer' : 'default',
          }}
        >
          <div style={{ fontSize: 9, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 3 }}>Vendor item #</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: hasItemNum ? '#0F0F0F' : '#B0B0B0', fontStyle: hasItemNum ? 'normal' : 'italic' }}>
            {hasItemNum ? vi.vendor_item_number : 'Tap to edit'}
          </div>
        </button>

        {/* Last price cell */}
        <button
          onClick={() => canEditPrice && !isLocked ? onTapPrice() : null}
          style={{
            background: '#F8F8F8', border: '1px solid #E8E8E8', borderRadius: 8,
            padding: '8px 10px', textAlign: 'left', cursor: canEditPrice && !isLocked ? 'pointer' : 'default',
          }}
        >
          <div style={{ fontSize: 9, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 3 }}>Last price</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: isLocked ? '#3C3489' : (hasPrice ? '#0F0F0F' : '#B0B0B0'), fontStyle: hasPrice ? 'normal' : 'italic' }}>
            {unitCostDisplay || 'Tap to edit'}
          </div>
          {packContextDisplay && (
            <div style={{ fontSize: 10, color: '#9A9A9A', marginTop: 1 }}>{packContextDisplay}</div>
          )}
        </button>
      </div>

      {/* Action row */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {canTogglePreferred && (
          <button
            onClick={onTogglePreferred}
            disabled={saving}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '5px 12px',
              background: isPreferred ? '#E1F5EE' : 'white',
              color: isPreferred ? '#085041' : '#7A7A7A',
              border: `1px solid ${isPreferred ? '#5DCAA5' : '#E0E0E0'}`,
              borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            }}
          >
            <IconStar size={11} />
            {isPreferred ? 'Preferred' : 'Set preferred'}
          </button>
        )}
        {canRemove && (
          <button
            onClick={onRemove}
            style={{
              marginLeft: 'auto', background: 'none', border: 'none',
              color: '#D0D0D0', cursor: 'pointer', padding: 4,
              display: 'flex', alignItems: 'center',
            }}
            onTouchStart={e => e.currentTarget.style.color = '#A32D2D'}
            onTouchEnd={e => e.currentTarget.style.color = '#D0D0D0'}
          >
            <IconX size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── PRICE SHEET (mobile) ──────────────────────────────────────────────────────

function PriceSheet({ visible, vi, itemUom, onClose, onSaved }) {
  const [price, setPrice] = useState('');
  const [packQty, setPackQty] = useState('');
  const [packUnit, setPackUnit] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Sheet on a phone, centred modal on a desktop. The resize-listener pattern
  // from Session 67, not a plain window.innerWidth read, which never updates.
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (visible && vi) {
      setPrice(vi.vendor_price ? String(vi.vendor_price) : '');
      setPackQty(vi.pack_qty ? String(vi.pack_qty) : '');
      setPackUnit(vi.pack_unit || '');
      setError('');
    }
  }, [visible, vi?.id]);

  if (!visible || !vi) return null;

  const itemName = vi.inventory_items?.name || '—';
  const vendorName = vi.inventory_vendors?.name || '';

  // #393. The convention, stated once and applied to all three price forms:
  // the number typed here is what the VENDOR CHARGES FOR ONE PACK, and the
  // per-unit cost is derived from it. That is not a preference — vendor_price is
  // divided by pack_qty by the database to produce unit_cost, so a pack price is
  // what the column has always meant. It is also SAP's model (a net price plus
  // the price unit the amount is valid for) and NetSuite's (last purchase price
  // in purchase units, average cost in stock units).
  //
  // Before this, two forms stored what was typed and a third multiplied it up,
  // and NOTHING on screen said which was being asked. Whoever learned one form
  // first got the other wrong every time, and the shelf value inflated by the
  // pack factor.
  const priceNum = price === '' ? null : parseFloat(price);
  const qtyNum = packQty === '' ? null : parseInt(packQty, 10);
  const priceOk = priceNum != null && !isNaN(priceNum) && priceNum > 0;
  const qtyOk = qtyNum != null && !isNaN(qtyNum) && qtyNum > 0;

  // #393 door 2, inverted safety net. The green confirmation used to render only
  // when BOTH fields were filled, so in the exact case that goes wrong — pack qty
  // left blank — the person saw nothing at all. The reassurance appeared only
  // once you had already done it right. Now the arithmetic shows the moment it
  // CAN be shown, including when the quantity is 1 and the division changes
  // nothing, because that is the case it most needs to state out loud.
  const showCalc = priceOk && qtyOk;
  const calcUnitCost = showCalc ? (priceNum / qtyNum).toFixed(2) : null;
  const saveBlocked = priceOk && (!qtyOk || !packUnit);
  const stockUnit = itemUom || 'unit';
  const sellUnit = packUnit || 'pack';

  async function handleSave() {
    setError('');
    // Pack qty stops being optional. It used to coerce a blank to 1, so a $600
    // pallet price became $600 per sheet with nothing on screen to say so. It is
    // the divisor for inventory value; a missing one is not a default, it is an
    // unanswered question.
    if (priceOk && !qtyOk) {
      setError(`Enter how many ${stockUnit} come in one ${sellUnit}. It sets what the stock is worth.`);
      return;
    }
    if (priceOk && !packUnit) {
      setError('Pick what the vendor sells this by.');
      return;
    }
    setSaving(true);
    try {
      await supabase.from('vendor_items').update({
        vendor_price: priceNum,
        pack_qty: qtyOk ? qtyNum : 1,
        pack_unit: packUnit || null,
        price_date: priceOk ? new Date().toISOString() : vi.price_date,
      }).eq('id', vi.id);
      onSaved();
    } catch (e) {
      setError('Could not save price.');
      setSaving(false);
    }
  }

  return (
    // Was a hand-rolled position:fixed overlay, which is locked rule 3 and is
    // also why this sheet came up as a full-width band pinned to the bottom of a
    // desktop screen. One of #148 Split A's VendorCatalogPage instances, closed
    // here rather than at Sep 28-29 because this form was being rebuilt anyway
    // and leaving it would have meant opening the file twice.
    <Overlay variant={isMobile ? 'sheet' : 'modal'} onClose={onClose} maxWidth={460}>
      <div style={{ padding: '16px 20px 24px', maxHeight: isMobile ? undefined : '80vh', overflowY: 'auto' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F', marginBottom: 2 }}>
          Vendor price
        </div>
        <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 20 }}>{itemName} · {vendorName}</div>

        {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}

        {/* Sells-by comes FIRST now, and the order is the fix rather than a
            layout preference: you cannot answer "what does one cost" until you
            have said one WHAT. Asking for the price first is what let someone
            type a per-sheet number into a field that meant per-pallet. */}
        <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>
          Vendor sells by
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 8, marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: 10, color: '#9A9A9A', marginBottom: 4 }}>How many {stockUnit}</div>
            <input
              type="number" min="1"
              value={packQty}
              onChange={e => setPackQty(e.target.value)}
              placeholder="1"
              style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', fontSize: 16, boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#9A9A9A', marginBottom: 4 }}>In one</div>
            <UnitSelect
              kind="package"
              value={packUnit}
              onChange={setPackUnit}
              placeholder="pack type"
              ariaLabel="What the vendor sells this by"
            />
          </div>
        </div>
        {itemUom && (
          <div style={{ fontSize: 11, color: '#9A9A9A', fontStyle: 'italic', marginBottom: 16 }}>
            Inventory counts this item in {itemUom}.
          </div>
        )}

        <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>
          What the vendor charges for one {sellUnit}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', height: 48, border: '2px solid #1D9E75', borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
          <span style={{ padding: '0 12px', fontSize: 16, color: '#7A7A7A', background: '#F5F5F3', height: '100%', display: 'flex', alignItems: 'center', borderRight: '1px solid #E0E0E0', fontWeight: 600 }}>$</span>
          <input
            type="number" step="0.01" min="0"
            value={price}
            onChange={e => setPrice(e.target.value)}
            autoFocus
            placeholder="0.00"
            // minWidth 0 so the input can actually shrink. An input carries a
            // default intrinsic width that flex:1 alone will not override, so at
            // 375px the unit suffix beside it was being clipped to "/pa".
            style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', fontSize: 18, padding: '0 12px', fontWeight: 600 }}
          />
          <span style={{ padding: '0 12px', fontSize: 13, color: '#7A7A7A', flexShrink: 0, whiteSpace: 'nowrap' }}>/{sellUnit}</span>
        </div>

        {showCalc ? (
          <div style={{
            background: '#E1F5EE', borderRadius: 8, padding: '8px 12px',
            fontSize: 12, color: '#085041', fontWeight: 600, marginBottom: 16,
          }}>
            ${priceNum.toFixed(2)} ÷ {qtyNum} = ${calcUnitCost} per {stockUnit} — this is what the stock is valued at
          </div>
        ) : priceOk ? (
          <div style={{
            background: '#FFF8EC', borderRadius: 8, padding: '8px 12px',
            fontSize: 12, color: '#9A5F00', fontWeight: 600, marginBottom: 16,
          }}>
            Say how many {stockUnit} come in one {sellUnit}. Without it, ${priceNum.toFixed(2)} would be recorded as the cost of a single {stockUnit}.
          </div>
        ) : null}

        {/* Blocked, not merely refused on click - the same rule the desktop row
            uses, so the two forms cannot teach different lessons. The amber
            warning above names the missing half. */}
        <button
          onClick={handleSave}
          disabled={saving || saveBlocked}
          style={{ width: '100%', padding: '13px 0', background: (saving || saveBlocked) ? '#E0E0E0' : '#0F0F0F', color: (saving || saveBlocked) ? '#9A9A9A' : 'white', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: (saving || saveBlocked) ? 'not-allowed' : 'pointer', marginBottom: 8 }}
        >
          {saving ? 'Saving...' : 'Save price'}
        </button>
        <button onClick={onClose} style={{ width: '100%', background: 'none', border: 'none', fontSize: 13, color: '#7A7A7A', cursor: 'pointer', padding: 4 }}>
          Cancel
        </button>
      </div>
    </Overlay>
  );
}

// ── ITEM NUM SHEET (mobile) ───────────────────────────────────────────────────

function ItemNumSheet({ visible, vi, onClose, onSaved }) {
  // With the other hooks and ABOVE the `if (!visible)` early return below --
  // a hook called on some renders and not others breaks the order React relies
  // on, and it fails the BUILD rather than a test.
  const { tier } = useTier();
  const [itemNum, setItemNum] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (visible && vi) {
      setItemNum(vi.vendor_item_number || '');
      setError('');
    }
  }, [visible, vi?.id]);

  if (!visible || !vi) return null;

  const itemName = vi.inventory_items?.name || '—';
  const vendorName = vi.inventory_vendors?.name || '';

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await supabase.from('vendor_items').update({
        vendor_item_number: itemNum.trim() || null,
      }).eq('id', vi.id);
      onSaved();
    } catch (e) {
      setError('Could not save.');
      setSaving(false);
    }
  }

  // #505 and locked rule 3, fixed at Session 79.15b under #417's D24.
  //
  // THIS HAND-ROLLED ITS OWN `position: fixed` OVERLAY, which is locked rule 3,
  // and pinned itself to `alignItems: 'flex-end'` so it was a bottom sheet at
  // every width it could reach.
  //
  // A CORRECTION TO THE GATE, recorded rather than quietly fixed: #417 lists
  // this as "a bottom sheet on a 1440px monitor", and that is FALSE. It renders
  // only inside VendorCatalogPage's `isMobile` branch, so a desktop never sees
  // it at all. The work is still right -- the rule 3 violation is real, and
  // under #417's tiers a TABLET now takes this branch too -- but the reason in
  // the gate was wrong. Amended in the gate itself, per the amendment protocol.
  return (
    <Overlay
      variant={usesPhoneLayout(tier) ? 'sheet' : 'modal'}
      onClose={onClose}
      maxWidth={460}
      panelStyle={{ maxHeight: '60vh', display: 'flex', flexDirection: 'column' }}
    >
      <>
        <div style={{ width: 36, height: 4, background: '#D0D0D0', borderRadius: 2, margin: '12px auto 0', flexShrink: 0 }} />
        <div style={{ padding: '16px 20px 40px', overflowY: 'auto' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F', marginBottom: 2 }}>Vendor item #</div>
          <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 20 }}>{itemName} · {vendorName}</div>

          {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}

          <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Item number</div>
          <input
            type="text"
            value={itemNum}
            onChange={e => setItemNum(e.target.value)}
            autoFocus
            placeholder="e.g. GR-3MVHB1"
            style={{ width: '100%', height: 48, border: '2px solid #1D9E75', borderRadius: 10, padding: '0 14px', fontSize: 18, fontWeight: 600, color: '#0F0F0F', marginBottom: 20, boxSizing: 'border-box' }}
          />

          <button
            onClick={handleSave}
            disabled={saving}
            style={{ width: '100%', padding: '13px 0', background: saving ? '#E0E0E0' : '#0F0F0F', color: saving ? '#9A9A9A' : 'white', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', marginBottom: 8 }}
          >
            {saving ? 'Saving...' : 'Save item #'}
          </button>
          <button onClick={onClose} style={{ width: '100%', background: 'none', border: 'none', fontSize: 13, color: '#7A7A7A', cursor: 'pointer', padding: 4 }}>
            Cancel
          </button>
        </div>
      </>
    </Overlay>
  );
}

// ── VENDOR DETAIL PANEL ───────────────────────────────────────────────────────

function VendorDetailPanel({
  vendor,
  allVendorItems,
  isMobile,
  onBack,
  onVendorUpdated,
  setActive,
  setSelectedItemId,
  filterItemId,
  filterItemName,
  onShowPriceHistory,
  canReview,
  needsReviewVendors,
  onSelectVendor,
}) {
  const { profile, isAdmin, isManager, isExecutive, isPurchasing } = useAuth();
  const canEdit = canEditVendor({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  const canDeactivate = canDeactivateVendor({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  const canLock = canLockVendor({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  const canTogglePreferred = canTogglePreferredVendor({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  const canEditPrice = canEditVendorPrice({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  const canContact = canActionVendorCatalog({ isAdmin, isManager, isExecutive, isPurchasing, profile });

  const [reviewing, setReviewing] = useState(false);

  async function markReviewed() {
    if (!vendor) return;
    setReviewing(true);
    try {
      await supabase.from('inventory_vendors').update({
        reviewed_at: new Date().toISOString(),
        reviewed_by: profile?.id,
      }).eq('id', vendor.id);
      onVendorUpdated();
    } catch (e) {
      setError('Could not mark reviewed: ' + e.message);
    } finally {
      setReviewing(false);
    }
  }

  function goToNextUnreviewed() {
    const next = (needsReviewVendors || []).find(v => v.id !== vendor?.id);
    if (next && onSelectVendor) onSelectVendor(next);
  }

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editItemNum, setEditItemNum] = useState('');
  const [editVendorPrice, setEditVendorPrice] = useState('');
  const [editPackQty, setEditPackQty] = useState('');
  const [editPackUnit, setEditPackUnit] = useState('');
  const [rowSaving, setRowSaving] = useState(false);
  const [mobilePriceSheet, setMobilePriceSheet] = useState({ open: false, vi: null });
  const [mobileItemNumSheet, setMobileItemNumSheet] = useState({ open: false, vi: null });
  const [showAssociate, setShowAssociate] = useState(false);

  useEffect(() => {
    if (vendor) {
      setForm({
        contact_name: vendor.contact_name || '',
        phone: vendor.phone || '',
        email: vendor.email || '',
        notes: vendor.notes || '',
      });
      setEditing(false);
      setError('');
      setConfirmDeactivate(false);
      setShowAssociate(false);
    }
  }, [vendor?.id]);

  if (!vendor) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        height: '100%', color: '#9A9A9A', gap: 8, padding: 32,
      }}>
        <IconBuildingStore size={32} color="#D0D0D0" />
        <div style={{ fontSize: 13 }}>Select a vendor to view details</div>
      </div>
    );
  }

  // #422 / M16, Session 78s.1. TWO lists, not one, and the split is what stops
  // an import of four hundred rows filling this page with em dashes.
  //
  // "Items supplied" reads `inventory_items.name`, so a row with no item — a
  // product the vendor sells that D&R does not stock — rendered as "—" with a
  // price beside it. That was tolerable while the only such rows came from
  // off-catalog orders, a handful at a time. It stops being tolerable the
  // moment a supplier's whole price list can be loaded in one press.
  const allRowsForVendor = allVendorItems.filter(vi => vi.vendor_id === vendor.id);
  const vendorItems = allRowsForVendor.filter(vi => vi.item_id != null);
  const vendorOwnCatalog = allRowsForVendor.filter(vi => vi.item_id == null);
  const existingItemIds = vendorItems.map(vi => vi.item_id);

  async function saveContact() {
    setSaving(true);
    setError('');
    try {
      const { error: err } = await supabase
        .from('inventory_vendors')
        .update({
          contact_name: form.contact_name.trim() || null,
          phone: form.phone.trim() || null,
          email: form.email.trim() || null,
          notes: form.notes.trim() || null,
        })
        .eq('id', vendor.id);
      if (err) throw err;
      setEditing(false);
      onVendorUpdated();
    } catch (e) {
      setError('Could not save: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivate() {
    setSaving(true);
    try {
      await supabase.from('inventory_vendors').update({ is_active: false }).eq('id', vendor.id);
      setConfirmDeactivate(false);
      onVendorUpdated();
    } catch (e) {
      setError('Could not deactivate vendor.');
    } finally {
      setSaving(false);
    }
  }

  async function handleReactivate() {
    setSaving(true);
    try {
      await supabase.from('inventory_vendors').update({ is_active: true }).eq('id', vendor.id);
      onVendorUpdated();
    } catch (e) {
      setError('Could not reactivate vendor.');
    } finally {
      setSaving(false);
    }
  }

  async function handleTogglePreferred(vi) {
    if (!canTogglePreferred) return;
    try {
      if (!vi.is_preferred) {
        const sameItem = allVendorItems.filter(v => v.item_id === vi.item_id && v.id !== vi.id && v.is_preferred);
        for (const other of sameItem) {
          await supabase.from('vendor_items').update({ is_preferred: false }).eq('id', other.id);
        }
      }
      await supabase.from('vendor_items').update({ is_preferred: !vi.is_preferred }).eq('id', vi.id);
      onVendorUpdated();
    } catch (e) {
      setError('Could not update preferred status.');
    }
  }

  async function handleToggleLock(vi) {
    if (!canLock) return;
    try {
      await supabase.from('vendor_items').update({
        price_locked: !vi.price_locked,
        locked_by: vi.price_locked ? null : profile?.id,
      }).eq('id', vi.id);
      onVendorUpdated();
    } catch (e) {
      setError('Could not update lock.');
    }
  }

  function openRowEdit(vi) {
    setEditingRowId(vi.id);
    setEditItemNum(vi.vendor_item_number || '');
    setEditVendorPrice(vi.vendor_price ? String(vi.vendor_price) : '');
    setEditPackQty(vi.pack_qty ? String(vi.pack_qty) : '');
    setEditPackUnit(vi.pack_unit || '');
  }

  function cancelRowEdit() {
    setEditingRowId(null);
    setEditItemNum('');
    setEditVendorPrice('');
    setEditPackQty('');
    setEditPackUnit('');
  }

  async function handleSaveRow(vi) {
    setRowSaving(true);
    try {
      // #393. Same convention as PriceSheet and VendorItemsPanel: the number is
      // what the vendor charges for ONE PACK, and pack qty is the divisor that
      // turns it into a per-unit cost. Pack qty is no longer coerced from blank
      // to 1 - that coercion is how a $600 pallet price became $600 per sheet
      // with nothing on screen to say so.
      const price = editVendorPrice ? parseFloat(editVendorPrice) : null;
      const qtyEntered = editPackQty ? parseInt(editPackQty) : null;
      const priceOk = price != null && !isNaN(price) && price > 0;
      const qtyOk = qtyEntered != null && !isNaN(qtyEntered) && qtyEntered > 0;
      // Read off the row being saved. The itemUom the render uses is declared
      // inside the .map() callback and is NOT in scope here - referencing it
      // would throw at the moment this branch fires, which is the one branch
      // nobody exercises until they get it wrong.
      const rowUom = vi.inventory_items?.uom || 'units';
      if (priceOk && !qtyOk) {
        setError('Enter how many ' + rowUom + ' come in one pack before saving a price.');
        setRowSaving(false);
        return;
      }
      if (priceOk && !editPackUnit) {
        setError('Pick what the vendor sells this by before saving a price.');
        setRowSaving(false);
        return;
      }
      await supabase.from('vendor_items').update({
        vendor_item_number: editItemNum.trim() || null,
        vendor_price: price,
        pack_qty: qtyOk ? qtyEntered : 1,
        pack_unit: editPackUnit || null,
        price_date: priceOk ? new Date().toISOString() : vi.price_date,
      }).eq('id', vi.id);
      cancelRowEdit();
      onVendorUpdated();
    } catch (e) {
      setError('Could not save.');
    } finally {
      setRowSaving(false);
    }
  }

  function handleAssociateSaved() {
    setShowAssociate(false);
    onVendorUpdated();
  }

  async function handleRemoveItem(vi) {
    if (!window.confirm(`Remove ${vi.inventory_items?.name || 'this item'} from ${vendor.name}?`)) return;
    try {
      const { error: err } = await supabase.from('vendor_items').delete().eq('id', vi.id);
      if (err) throw err;
      onVendorUpdated();
    } catch (e) {
      setError('Could not remove item: ' + e.message);
    }
  }

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const lockedItems = vendorItems.filter(vi => vi.price_locked);
  const lockedByName = lockedItems[0]?.users?.full_name || null;

  return (
    <div style={{ padding: '16px 20px', overflowY: 'auto', height: '100%', position: 'relative' }}>

      {/* Back button (mobile only) */}
      {isMobile && (
        <button
          onClick={onBack}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            background: 'none', border: 'none', color: '#5C5C5C',
            fontSize: 12, cursor: 'pointer', padding: 0, marginBottom: 10,
          }}
        >
          <IconChevronLeft size={14} />
          Vendors
        </button>
      )}

      {/* Review banner — new vendor pending governance review, Issue #190 */}
      {canReview && !vendor.reviewed_at && (
        <div style={{
          background: '#FFF8EC', border: '1px solid #EF9F27', borderLeft: '3px solid #EF9F27',
          borderRadius: 8, padding: '10px 12px', marginBottom: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#633806', marginBottom: 4 }}>
            <IconAlertTriangle size={14} />
            New vendor — needs review
          </div>
          {vendor.similarity_warning_note && (
            <div style={{ fontSize: 11, color: '#633806', marginBottom: 10 }}>
              {vendor.similarity_warning_note}.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={markReviewed}
              disabled={reviewing}
              style={{ padding: '6px 12px', background: reviewing ? '#E0E0E0' : '#0F0F0F', color: reviewing ? '#9A9A9A' : 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: reviewing ? 'not-allowed' : 'pointer' }}
            >
              {reviewing ? 'Saving...' : 'Mark reviewed'}
            </button>
            {(needsReviewVendors || []).filter(v => v.id !== vendor.id).length > 0 && (
              <button
                onClick={goToNextUnreviewed}
                style={{ padding: '6px 12px', background: 'white', color: '#0F0F0F', border: '1px solid #E0E0E0', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
              >
                Next unreviewed →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Deep-link context banner — associate this item with vendor */}
      {filterItemId && filterItemName && (() => {
        const alreadySupplies = allVendorItems.some(vi => vi.vendor_id === vendor.id && vi.item_id === filterItemId);
        if (alreadySupplies) return (
          <div style={{
            background: '#E1F5EE', border: '1px solid #5DCAA5',
            borderRadius: 8, padding: '10px 14px', marginBottom: 14,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <IconCheck size={14} color="#085041" style={{ flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: '#085041', fontWeight: 600 }}>
              {vendor.name} already supplies {filterItemName}
            </span>
          </div>
        );
        return (
          <div style={{
            background: '#F0F8FF', border: '1px solid #B5D4F4',
            borderRadius: 8, padding: '10px 14px', marginBottom: 14,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0C447C', marginBottom: 6 }}>
              Associate {filterItemName} with {vendor.name}?
            </div>
            <div style={{ fontSize: 11, color: '#185FA5', marginBottom: 10 }}>
              This will link the item to this vendor and allow it to be used in purchasing flows.
            </div>
            <button
              onClick={async () => {
                try {
                  const { error: err } = await supabase.from('vendor_items').insert({
                    vendor_id: vendor.id,
                    item_id: filterItemId,
                    is_preferred: false,
                    price_locked: false,
                  });
                  if (err) throw err;
                  onVendorUpdated();
                  if (setSelectedItemId) setSelectedItemId(filterItemId);
                  setActive('inv_detail');
                } catch (e) {
                  // error handled by parent
                }
              }}
              style={{
                width: '100%', height: 36,
                background: '#0C447C', color: 'white',
                border: 'none', borderRadius: 7,
                fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Associate {filterItemName} with this vendor →
            </button>
          </div>
        );
      })()}

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: 12, gap: 8,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0F0F0F', marginBottom: 4 }}>
            {vendor.name}
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 10, fontWeight: 700,
              background: vendor.is_active ? '#E1F5EE' : '#F0F0F0',
              color: vendor.is_active ? '#085041' : '#9A9A9A',
              borderRadius: 4, padding: '2px 7px',
            }}>
              {vendor.is_active ? 'Active' : 'Inactive'}
            </span>
            {vendor.is_internal && (
              <span style={{
                fontSize: 10, fontWeight: 700,
                background: '#EEEDFE', color: '#3C3489',
                borderRadius: 4, padding: '2px 7px',
              }}>
                Internal
              </span>
            )}
          </div>
        </div>
        {canEdit && (
          <button
            onClick={() => setEditing(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '6px 12px',
              background: editing ? '#0F0F0F' : 'white',
              color: editing ? 'white' : '#0F0F0F',
              border: '1px solid #E0E0E0', borderRadius: 7,
              fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
            }}
          >
            <IconEdit size={13} />
            {editing ? 'Cancel' : 'Edit'}
          </button>
        )}
      </div>

      {error && (
        <div style={{
          background: '#FFEBEE', color: '#A32D2D', borderRadius: 8,
          padding: '8px 12px', fontSize: 12, marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <IconAlertTriangle size={13} />
          {error}
        </div>
      )}

      {/* ── CONTACT SECTION ── */}
      {!canContact && (
        <div style={{ background: '#F8F8F8', borderRadius: 8, border: '1px solid #E8E8E8', padding: '12px 14px', marginBottom: 16, fontSize: 12, color: '#9A9A9A', fontStyle: 'italic' }}>
          No contact access
        </div>
      )}
      {canContact && (
      <div style={{
        background: '#F8F8F8', borderRadius: 8,
        border: '1px solid #E8E8E8', padding: '12px 14px', marginBottom: 16,
      }}>
        {editing ? (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '8px 12px', alignItems: 'center', marginBottom: 8 }}>
              <span style={labelStyle}>Contact</span>
              <input type="text" value={form.contact_name} onChange={e => f('contact_name', e.target.value)}
                placeholder="Contact name" style={inputStyle} />
              <span style={labelStyle}>Phone</span>
              <input type="tel" value={form.phone} onChange={e => f('phone', e.target.value)}
                placeholder="800-000-0000" style={inputStyle} />
              <span style={labelStyle}>Email</span>
              <input type="email" value={form.email} onChange={e => f('email', e.target.value)}
                placeholder="orders@vendor.com" style={inputStyle} />
              <span style={labelStyle}>Notes</span>
              <textarea value={form.notes} onChange={e => f('notes', e.target.value)}
                placeholder="e.g. Net 30. Call before 4pm." rows={2}
                style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditing(false)}
                style={{ padding: '6px 14px', background: 'white', color: '#7A7A7A', border: '1px solid #E0E0E0', borderRadius: 7, fontSize: 12, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={saveContact} disabled={saving}
                style={{ padding: '6px 14px', background: saving ? '#E0E0E0' : '#1D9E75', color: saving ? '#9A9A9A' : 'white', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                <IconCheck size={13} />
                {saving ? 'Saving...' : 'Save contact'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '6px 12px', alignItems: 'baseline' }}>
            <span style={labelStyle}>Contact</span>
            <span style={valueStyle}>{vendor.contact_name || '—'}</span>
            <span style={labelStyle}>Phone</span>
            <a href={vendor.phone ? `tel:${vendor.phone}` : undefined}
              style={{ ...valueStyle, color: vendor.phone ? '#1D9E75' : '#9A9A9A', textDecoration: 'none' }}>
              {vendor.phone || '—'}
            </a>
            <span style={labelStyle}>Email</span>
            <a href={vendor.email ? `mailto:${vendor.email}` : undefined}
              style={{ ...valueStyle, color: vendor.email ? '#1D9E75' : '#9A9A9A', textDecoration: 'none' }}>
              {vendor.email || '—'}
            </a>
            {vendor.notes && (
              <>
                <span style={labelStyle}>Notes</span>
                <span style={{ ...valueStyle, color: '#5C5C5C', fontStyle: 'italic' }}>{vendor.notes}</span>
              </>
            )}
          </div>
        )}
      </div>
      )}

      {/* ── ITEMS SUPPLIED ── */}
      <div style={{ marginBottom: 16 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10,
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: '#7A7A7A',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            Items supplied
          </div>
          {canEdit && (
            <button
              onClick={() => setShowAssociate(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', background: 'white', color: '#0F0F0F',
                border: '1px solid #E0E0E0', borderRadius: 6,
                fontSize: 11, fontWeight: 600, cursor: 'pointer',
              }}
            >
              <IconPlus size={12} />
              Associate item
            </button>
          )}
        </div>

        {vendorItems.length === 0 ? (
          <div style={{
            background: '#F5F5F3', borderRadius: 8, padding: '12px 14px',
            fontSize: 12, color: '#9A9A9A', fontStyle: 'italic',
          }}>
            No items associated with this vendor yet.
          </div>
        ) : isMobile ? (
          // ── MOBILE: stacked cards ──────────────────────────────────
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {vendorItems.map(vi => (
              <MobileVendorItemCard
                key={vi.id}
                vi={vi}
                onTapPrice={() => setMobilePriceSheet({ open: true, vi })}
                onTapItemNum={() => setMobileItemNumSheet({ open: true, vi })}
                onTogglePreferred={() => handleTogglePreferred(vi)}
                onRemove={() => handleRemoveItem(vi)}
                canEditPrice={canEditPrice}
                canTogglePreferred={canTogglePreferred}
                canRemove={canEdit}
                saving={saving}
              />
            ))}
            <PriceSheet
              visible={mobilePriceSheet.open}
              vi={mobilePriceSheet.vi}
              itemUom={mobilePriceSheet.vi?.inventory_items?.uom || ''}
              onClose={() => setMobilePriceSheet({ open: false, vi: null })}
              onSaved={() => { setMobilePriceSheet({ open: false, vi: null }); onVendorUpdated(); }}
            />
            <ItemNumSheet
              visible={mobileItemNumSheet.open}
              vi={mobileItemNumSheet.vi}
              onClose={() => setMobileItemNumSheet({ open: false, vi: null })}
              onSaved={() => { setMobileItemNumSheet({ open: false, vi: null }); onVendorUpdated(); }}
            />
          </div>
        ) : (
          // ── DESKTOP: grid table ────────────────────────────────────
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Header row — always visible */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 100px 110px 90px 70px 36px',
              gap: 8, padding: '4px 8px',
              fontSize: 10, fontWeight: 700, color: '#9A9A9A',
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              <span>Item</span>
              <span>Vendor item #</span>
              <span>Last price</span>
              <span>Preferred</span>
              <span>Locked</span>
              <span></span>
            </div>

            {vendorItems.map(vi => {
              const itemName = vi.inventory_items?.name || '—';
              const itemUom = vi.inventory_items?.uom || '';
              const isLocked = vi.price_locked;
              const isPreferred = vi.is_preferred;
              const isEditingThis = editingRowId === vi.id;

              // Computed unit cost for calc pill (live while editing)
              const livePrice = isEditingThis ? editVendorPrice : (vi.vendor_price ? String(vi.vendor_price) : '');
              const liveQty = isEditingThis ? editPackQty : (vi.pack_qty ? String(vi.pack_qty) : '');
              const liveUnit = isEditingThis ? editPackUnit : (vi.pack_unit || '');
              const showCalcPill = livePrice && liveQty && parseFloat(livePrice) > 0 && parseInt(liveQty) > 0;
              // A price with no pack size, or no pack unit, cannot be saved -
              // pack qty is the divisor that turns it into a per-unit cost.
              // Blocking the BUTTON rather than only refusing on click is the
              // point: handleSaveRow's error banner renders at the top of the
              // vendor panel, roughly 400px above this row, so on a laptop the
              // person would click Save, see nothing move, and have no idea why.
              // The amber pill two lines below already names the missing half.
              const rowSaveBlocked = parseFloat(livePrice) > 0 && (!(parseInt(liveQty) > 0) || !liveUnit);
              const calcUnitCost = showCalcPill
                ? (parseFloat(livePrice) / parseInt(liveQty)).toFixed(2)
                : null;

              if (isEditingThis) {
                // ── EDIT STATE: 2-column layout ──────────────────────────
                return (
                  <div
                    key={vi.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '180px 1fr',
                      gap: 12, padding: '10px 10px',
                      background: '#FAFAF9',
                      borderRadius: 6,
                      border: '1px solid #1D9E75',
                      alignItems: 'start', marginBottom: 2,
                    }}
                  >
                    {/* Left: item name + preferred badge */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 2 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#0F0F0F', lineHeight: 1.3 }}>
                        {itemName}
                      </div>
                      {isPreferred && (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          fontSize: 10, fontWeight: 700,
                          background: '#E1F5EE', color: '#085041',
                          borderRadius: 4, padding: '1px 6px', width: 'fit-content',
                        }}>
                          <IconStar size={9} /> Preferred
                        </span>
                      )}
                    </div>

                    {/* Right: edit fields */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {/* Row 1: Vendor item # + Price */}
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 3 }}>Vendor item #</div>
                          <input
                            type="text"
                            value={editItemNum}
                            onChange={e => setEditItemNum(e.target.value)}
                            placeholder="e.g. GR-3MVHB1"
                            style={{ height: 28, border: '1px solid #1D9E75', borderRadius: 5, padding: '0 7px', width: 110, color: '#0F0F0F' }}
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 3 }}>$ per pack</div>
                          <div style={{ display: 'flex', alignItems: 'center', height: 28, border: '1px solid #1D9E75', borderRadius: 5, overflow: 'hidden', width: 90 }}>
                            <span style={{ padding: '0 5px', fontSize: 11, color: '#7A7A7A', background: '#F5F5F3', height: '100%', display: 'flex', alignItems: 'center', borderRight: '1px solid #E0E0E0' }}>$</span>
                            <input
                              type="number" step="0.01" min="0"
                              value={editVendorPrice}
                              onChange={e => setEditVendorPrice(e.target.value)}
                              autoFocus
                              placeholder="0.00"
                              style={{ flex: 1, border: 'none', outline: 'none', padding: '0 5px', width: 0 }}
                            />
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 3 }}>Units per pack</div>
                          <input
                            type="number" min="1"
                            value={editPackQty}
                            onChange={e => setEditPackQty(e.target.value)}
                            placeholder="24"
                            style={{ height: 28, border: '1px solid #E0E0E0', borderRadius: 5, padding: '0 7px', width: 56, color: '#0F0F0F' }}
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 3 }}>Sells by</div>
                          {/* Governed list, not free text. Compact styling is safe
                              here because this inline row is the DESKTOP path -
                              the mobile path is PriceSheet - so locked rule 12's
                              16px floor, which exists for iOS auto-zoom, does not
                              apply. It matches the sibling fields on this row. */}
                          <div style={{ width: 104 }}>
                            <UnitSelect
                              kind="package"
                              value={editPackUnit}
                              onChange={setEditPackUnit}
                              placeholder="pack type"
                              ariaLabel="What the vendor sells this by"
                              style={{ height: 28, fontSize: 12, borderRadius: 5, padding: '0 6px' }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Calc pill */}
                      {showCalcPill ? (
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          background: '#E1F5EE', borderRadius: 5, padding: '3px 8px',
                          fontSize: 11, color: '#085041', fontWeight: 600, width: 'fit-content',
                        }}>
                          ✓ ${calcUnitCost}/{itemUom || 'unit'} — this is what the stock is valued at
                          <span style={{ fontWeight: 400, color: '#3A8C68', fontSize: 10 }}>
                            ${parseFloat(livePrice).toFixed(2)}/{liveUnit || 'pack'} · {liveQty} {itemUom || 'units'}
                          </span>
                        </div>
                      ) : (parseFloat(livePrice) > 0) ? (
                        /* #393's inverted safety net. The pill used to render only
                           when BOTH fields were filled, so it was invisible in the
                           exact case that goes wrong. Now the missing half is named. */
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          background: '#FFF8EC', borderRadius: 5, padding: '3px 8px',
                          fontSize: 11, color: '#9A5F00', fontWeight: 600, width: 'fit-content',
                        }}>
                          Say how many {itemUom || 'units'} are in one pack — without it,
                          ${parseFloat(livePrice).toFixed(2)} is recorded as the cost of ONE {itemUom || 'unit'}
                        </div>
                      ) : null}

                      {/* Save / Cancel */}
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => handleSaveRow(vi)}
                          disabled={rowSaving || rowSaveBlocked}
                          title={rowSaveBlocked ? 'Say how many units are in one pack, and what the vendor sells it by.' : undefined}
                          style={{ padding: '5px 14px', background: (rowSaving || rowSaveBlocked) ? '#E0E0E0' : '#0F0F0F', color: (rowSaving || rowSaveBlocked) ? '#9A9A9A' : 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: (rowSaving || rowSaveBlocked) ? 'not-allowed' : 'pointer' }}
                        >
                          {rowSaving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={cancelRowEdit}
                          style={{ padding: '5px 12px', background: 'white', color: '#7A7A7A', border: '1px solid #E0E0E0', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }

              // ── STATIC STATE: 6-column grid ──────────────────────────
              return (
                <div
                  key={vi.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 100px 110px 90px 70px 36px',
                    gap: 8, padding: '8px 8px',
                    background: isPreferred ? '#F0FFF8' : 'white',
                    borderRadius: 6,
                    border: `1px solid ${isPreferred ? '#5DCAA5' : '#F0F0F0'}`,
                    alignItems: 'center', marginBottom: 2,
                  }}
                >
                  {/* Item name */}
                  <div>
                    <button
                      onClick={() => {
                        if (setSelectedItemId && setActive) {
                          setSelectedItemId(vi.item_id);
                          setActive('inv_detail');
                        }
                      }}
                      style={{
                        background: 'none', border: 'none',
                        fontSize: 12, fontWeight: 600, color: '#0F0F0F',
                        cursor: setActive ? 'pointer' : 'default',
                        padding: 0, textAlign: 'left',
                        textDecoration: setActive ? 'underline' : 'none',
                        textDecorationColor: '#D0D0D0',
                      }}
                    >
                      {itemName}
                    </button>
                  </div>

                  {/* Vendor item # — clickable to open edit */}
                  <button
                    onClick={() => canEditPrice && !isLocked ? openRowEdit(vi) : null}
                    style={{
                      background: 'none', border: 'none', padding: 0, textAlign: 'left',
                      fontSize: 11, color: vi.vendor_item_number ? '#5C5C5C' : '#B0B0B0',
                      fontStyle: vi.vendor_item_number ? 'normal' : 'italic',
                      cursor: canEditPrice && !isLocked ? 'pointer' : 'default',
                      textDecoration: canEditPrice && !isLocked ? 'underline' : 'none',
                      textDecorationColor: '#D0D0D0',
                    }}
                  >
                    {vi.vendor_item_number ? `#${vi.vendor_item_number}` : '—'}
                  </button>

                  {/* Price column */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: isLocked ? '#3C3489' : '#0F0F0F' }}>
                        {vi.unit_cost != null
                          ? `$${parseFloat(vi.unit_cost).toFixed(2)}${itemUom ? '/' + itemUom : ''}`
                          : <span style={{ color: '#9A9A9A', fontWeight: 400, fontStyle: 'italic' }}>—</span>
                        }
                      </span>
                      {canEditPrice && !isLocked && (
                        <button
                          onClick={() => openRowEdit(vi)}
                          style={{ background: 'none', border: 'none', fontSize: 10, color: '#1D9E75', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    {vi.vendor_price != null && vi.pack_qty && vi.pack_unit && (
                      <div style={{ fontSize: 10, color: '#9A9A9A', marginTop: 1 }}>
                        ${parseFloat(vi.vendor_price).toFixed(2)}/{vi.pack_unit} · {vi.pack_qty} {itemUom || 'units'}
                      </div>
                    )}
                  </div>

                  {/* Preferred */}
                  <div>
                    {canTogglePreferred ? (
                      <button
                        onClick={() => handleTogglePreferred(vi)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          padding: '3px 8px',
                          background: isPreferred ? '#E1F5EE' : 'white',
                          color: isPreferred ? '#085041' : '#9A9A9A',
                          border: `1px solid ${isPreferred ? '#5DCAA5' : '#E0E0E0'}`,
                          borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                        }}
                      >
                        <IconStar size={9} />
                        {isPreferred ? 'Yes' : 'No'}
                      </button>
                    ) : (
                      <span style={{ fontSize: 11, color: isPreferred ? '#085041' : '#9A9A9A', fontWeight: isPreferred ? 700 : 400 }}>
                        {isPreferred ? '★ Yes' : '—'}
                      </span>
                    )}
                  </div>

                  {/* Locked */}
                  <div>
                    {canLock ? (
                      <button
                        onClick={() => handleToggleLock(vi)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          padding: '3px 8px',
                          background: isLocked ? '#EEEDFE' : 'white',
                          color: isLocked ? '#3C3489' : '#9A9A9A',
                          border: `1px solid ${isLocked ? '#AFA9EC' : '#E0E0E0'}`,
                          borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                        }}
                      >
                        <IconLock size={9} />
                        {isLocked ? 'Yes' : 'No'}
                      </button>
                    ) : (
                      <span style={{ fontSize: 11, color: isLocked ? '#3C3489' : '#9A9A9A', fontWeight: isLocked ? 700 : 400 }}>
                        {isLocked ? '🔒' : '—'}
                      </span>
                    )}
                  </div>

                  {/* Remove */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {canEdit && (
                      <button
                        onClick={() => handleRemoveItem(vi)}
                        title="Remove from vendor"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#D0D0D0', padding: 2, borderRadius: 4,
                          display: 'flex', alignItems: 'center',
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = '#A32D2D'}
                        onMouseLeave={e => e.currentTarget.style.color = '#D0D0D0'}
                      >
                        <IconX size={13} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {lockedItems.length > 0 && (
          <div style={{
            marginTop: 10, background: '#FAEEDA',
            border: '1px solid #EF9F27', borderRadius: 8,
            padding: '8px 12px', fontSize: 11, color: '#633806',
            display: 'flex', alignItems: 'flex-start', gap: 6,
          }}>
            <IconLock size={13} style={{ flexShrink: 0, marginTop: 1 }} />
            {lockedItems[0]?.inventory_items?.name} price locked
            {lockedByName ? ` by ${lockedByName}` : ''}.
            Contract pricing — purchasing cannot override.
          </div>
        )}
      </div>

      {/* ── THEIR CATALOG · #422 M16, Screen 5 ── */}
      <VendorOwnCatalogSection
        vendor={vendor}
        rows={vendorOwnCatalog}
        isMobile={isMobile}
        auth={{ isAdmin, isManager, isExecutive, isPurchasing, profile }}
        onChanged={onVendorUpdated}
      />

      {/* ── ACTION BUTTONS ── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <button
          onClick={() => {}}
          style={{
            padding: '8px 16px', background: 'white', color: '#0F0F0F',
            border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Order history
        </button>
        <button
          onClick={() => {}}
          style={{
            padding: '8px 16px', background: 'white', color: '#0F0F0F',
            border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}
        >
          View scorecard
        </button>
        {onShowPriceHistory && (
          <button
            onClick={() => onShowPriceHistory(vendor.id)}
            style={{
              padding: '8px 16px', background: 'none', color: '#1D9E75',
              border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#5DCAA5',
            }}
          >
            View price history →
          </button>
        )}
      </div>

      {/* ── DEACTIVATE ── */}
      {canDeactivate && (
        <div>
          {confirmDeactivate ? (
            <div style={{
              background: '#FFEBEE', border: '1px solid #F09595',
              borderRadius: 8, padding: '10px 12px',
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#A32D2D', marginBottom: 4 }}>
                Deactivate {vendor.name}?
              </div>
              <div style={{ fontSize: 11, color: '#791F1F', marginBottom: 10 }}>
                Vendor will be hidden from purchasing flows but all data is preserved.
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setConfirmDeactivate(false)}
                  style={{ flex: 1, padding: '7px 0', background: 'white', color: '#7A7A7A', border: '1px solid #E0E0E0', borderRadius: 7, fontSize: 12, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={handleDeactivate} disabled={saving}
                  style={{ flex: 1, padding: '7px 0', background: '#A32D2D', color: 'white', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Deactivating...' : 'Deactivate'}
                </button>
              </div>
            </div>
          ) : vendor.is_active ? (
            <button
              onClick={() => setConfirmDeactivate(true)}
              style={{
                padding: '8px 16px', background: 'white', color: '#A32D2D',
                border: '1px solid #F09595', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Deactivate vendor
            </button>
          ) : (
            <button
              onClick={handleReactivate}
              disabled={saving}
              style={{
                padding: '8px 16px', background: '#E1F5EE', color: '#085041',
                border: '1px solid #5DCAA5', borderRadius: 8, fontSize: 12, fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Reactivating...' : 'Reactivate vendor'}
            </button>
          )}
        </div>
      )}

      {/* ── ASSOCIATE ITEM SHEET ── */}
      <AssociateItemSheet
        visible={showAssociate}
        onClose={() => setShowAssociate(false)}
        onSaved={handleAssociateSaved}
        vendorId={vendor.id}
        existingItemIds={existingItemIds}
        allVendorItems={allVendorItems}
        isMobile={isMobile}
      />
    </div>
  );
}

// ── STYLE CONSTANTS ───────────────────────────────────────────────────────────

const labelStyle = {
  fontSize: 10, fontWeight: 700, color: '#9A9A9A',
  textTransform: 'uppercase', letterSpacing: '0.05em',
};

const valueStyle = {
  fontSize: 13, fontWeight: 600, color: '#0F0F0F',
};

const inputStyle = {
  width: '100%', height: 36,
  border: '1px solid #E0E0E0', borderRadius: 8,
  padding: '0 10px', color: '#0F0F0F',
  marginBottom: 10, boxSizing: 'border-box', background: 'white',
};

function FieldLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, color: '#7A7A7A',
      textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5,
    }}>
      {children}
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────

export default function VendorCatalogPage({ setActive, setSelectedItemId, filterItemId, filterItemName }) {
  const { profile, isAdmin, isManager, isExecutive, isPurchasing } = useAuth();
  const canAdd = canAddVendor({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  const canContact = canActionVendorCatalog({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  const canReview = canReviewVendor({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  // Not offered while the page is drilled down to a single item's vendors.
  // The export covers the CATALOG, and in that mode the screen is answering a
  // narrower question — "who supplies this item" — so a file of every vendor
  // would not be the thing the person was looking at. That mismatch between
  // what is on screen and what lands in the file is #394's whole defect class.
  const canExport = canExportVendorCatalog({ isAdmin, isManager, isExecutive, isPurchasing, profile })
    && !filterItemId;
  // The import is offered wherever the export is, and for the same reason:
  // in item-filter mode this page is answering "who supplies this item",
  // which is not the catalog the file describes.
  const canImport = canImportVendorCatalog({ isAdmin, isManager, isExecutive, isPurchasing, profile })
    && !filterItemId;

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const [vendors, setVendors] = useState([]);
  const [allVendorItems, setAllVendorItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);
  const [showPriceHistory, setShowPriceHistory] = useState(false);
  const [filterHistoryVendorId, setFilterHistoryVendorId] = useState(null);
  const [filterHistoryVendorName, setFilterHistoryVendorName] = useState(null);
  const selectedVendorIdRef = useRef(null);

  const setSelectedVendorAndRef = (v) => {
    selectedVendorIdRef.current = v?.id ?? null;
    setSelectedVendor(v);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: vendorData, error: vErr } = await supabase
        .from('inventory_vendors')
        .select(`
          id, name, contact_name, email, phone, is_internal,
          is_active, branch_id, notes, created_at,
          reviewed_at, reviewed_by, similarity_warning_note,
          vendor_items ( id, item_id, is_preferred, price_locked, vendor_price )
        `)
        .order('name', { ascending: true });
      if (vErr) throw vErr;

      const { data: viData, error: viErr } = await supabase
        .from('vendor_items')
        .select(`
          id, vendor_id, item_id, vendor_item_number, vendor_price,
          pack_qty, pack_unit, unit_cost,
          price_date, is_preferred, price_locked, locked_by,
          off_catalog_description, is_provisional,
          inventory_items ( id, name, sku, uom ),
          inventory_vendors ( id, name ),
          users!vendor_items_locked_by_fkey ( id, full_name )
        `);
      if (viErr) throw viErr;

      setVendors(vendorData || []);
      setAllVendorItems(viData || []);

      const currentId = selectedVendorIdRef.current;
      if (currentId && vendorData?.length > 0) {
        const reselect = vendorData.find(v => v.id === currentId);
        if (reselect) setSelectedVendor(reselect);
      } else if (!currentId && vendorData?.length > 0) {
        const first = vendorData.find(v => v.is_active) || vendorData[0];
        setSelectedVendorAndRef(first);
      }
    } catch (e) {
      setError('Could not load vendors: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const needsReviewVendors = vendors.filter(v => !v.reviewed_at);

  const filteredVendors = vendors.filter(v => {
    if (canReview && !v.reviewed_at) return false;
    if (search) {
      const q = search.toLowerCase();
      const matches = [v.name, v.contact_name, v.email, v.phone]
        .filter(Boolean)
        .some(s => s.toLowerCase().includes(q));
      if (!matches) return false;
    }
    if (!filterItemId) {
      if (statusFilter === 'Active' && !v.is_active) return false;
      if (statusFilter === 'Inactive' && v.is_active) return false;
    } else {
      if (!v.is_active) return false;
    }
    return true;
  });

  const vendorSuppliesFilterItem = (vendorId) =>
    filterItemId
      ? allVendorItems.some(vi => vi.vendor_id === vendorId && vi.item_id === filterItemId)
      : false;

  function handleVendorSelect(v) {
    setSelectedVendorAndRef(v);
    if (isMobile) setMobileShowDetail(true);
  }

  function handleVendorAdded(newVendor) {
    setShowAddVendor(false);
    setSelectedVendorAndRef(newVendor);
    load().then(() => {
      if (isMobile) setMobileShowDetail(true);
    });
  }

  const ListPanel = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid #E8E8E8', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F' }}>Vendors</div>
          <span style={{ fontSize: 11, color: '#7A7A7A' }}>
            {filterItemId
              ? `${filteredVendors.length} active vendors`
              : `${vendors.filter(v => v.is_active).length} active`
            }
          </span>
        </div>

        {filterItemId && filterItemName && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
            background: '#E6F1FB', borderRadius: 6, padding: '5px 10px', fontSize: 11,
          }}>
            <span style={{ color: '#0C447C', fontSize: 10 }}>🔽 Filtered by item:</span>
            <span style={{ fontWeight: 700, color: '#0C447C', flex: 1 }}>{filterItemName}</span>
            <button
              onClick={() => setActive('purchasing_vendors')}
              style={{ background: 'none', border: 'none', color: '#0C447C', cursor: 'pointer', fontSize: 12, fontWeight: 700, padding: 0 }}
            >
              ×
            </button>
          </div>
        )}

        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          border: '1px solid #E0E0E0', borderRadius: 8, padding: '6px 10px',
          marginBottom: 8, background: 'white',
        }}>
          <IconSearch size={14} color="#9A9A9A" />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search vendors..."
            style={{ flex: 1, border: 'none', outline: 'none', color: '#0F0F0F', background: 'transparent' }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              <IconX size={12} color="#9A9A9A" />
            </button>
          )}
        </div>

        {!filterItemId && (
          <div style={{ display: 'flex', gap: 4 }}>
            {STATUS_FILTERS.map(f => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                style={{
                  padding: '4px 10px', borderRadius: 20,
                  border: `1px solid ${statusFilter === f ? '#1D9E75' : '#E0E0E0'}`,
                  background: statusFilter === f ? '#E1F5EE' : 'white',
                  color: statusFilter === f ? '#085041' : '#7A7A7A',
                  fontSize: 11, fontWeight: statusFilter === f ? 700 : 400, cursor: 'pointer',
                }}
              >
                {f}
              </button>
            ))}
          </div>
        )}

        {/* #422, Session 78s — Add vendor moved here from the FOOTER of this
            panel, on Colby's report that it was invisible on a portrait
            monitor. It has to live inside this flexShrink:0 header rather
            than merely "above the first card": the block below is the
            scrolling region, so a control at the top of THAT would survive a
            short viewport and then disappear again once Dallas has forty
            vendors. This is #432's lesson, and #194 already applied it to
            Import/Export while leaving this button in the position its own
            comment calls unreachable. Filled rather than outlined because it
            is the one primary action in this column. */}
        {canAdd && (
          <button
            onClick={() => setShowAddVendor(true)}
            style={{
              width: '100%', marginTop: 8, padding: '8px 0',
              background: '#1D9E75', color: 'white',
              border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 6,
            }}
          >
            <IconPlus size={14} />
            Add vendor
          </button>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
        {!loading && !error && canReview && needsReviewVendors.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 10, fontWeight: 700, color: '#9A5F00',
              textTransform: 'uppercase', letterSpacing: '.5px',
              borderBottom: '2px solid #BA7517', paddingBottom: 6, marginBottom: 6,
            }}>
              Needs review
              <span style={{ background: '#9A5F00', color: 'white', borderRadius: 20, padding: '1px 6px', fontSize: 10 }}>
                {needsReviewVendors.length}
              </span>
            </div>
            {needsReviewVendors.map(v => (
              <VendorCard
                key={v.id} vendor={v}
                isSelected={selectedVendor?.id === v.id}
                onClick={() => handleVendorSelect(v)}
                isMobile={isMobile}
                canContact={canContact}
              />
            ))}
          </div>
        )}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#9A9A9A', fontSize: 13 }}>Loading...</div>
        ) : error ? (
          <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>
            {error}
          </div>
        ) : filteredVendors.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#9A9A9A', fontSize: 13 }}>
            {search ? 'No vendors match your search.' : 'No vendors yet.'}
          </div>
        ) : (
          filteredVendors.map(v => (
            <VendorCard
              key={v.id} vendor={v}
              isSelected={selectedVendor?.id === v.id}
              onClick={() => handleVendorSelect(v)}
              isMobile={isMobile}
              canContact={canContact}
            />
          ))
        )}
      </div>

    </div>
  );

  // ── MOBILE LAYOUT ──────────────────────────────────────────────────────────

  if (isMobile) {
    return (
      <div style={{ background: '#F5F5F3', minHeight: '100vh', paddingBottom: 80 }}>
        {filterItemId && filterItemName && (
          <div style={{ padding: '8px 14px', background: 'white', borderBottom: '1px solid #E8E8E8' }}>
            <button
              onClick={() => { if (setSelectedItemId) setSelectedItemId(filterItemId); setActive('inv_detail'); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                background: 'none', border: 'none', color: '#1D9E75',
                fontSize: 12, cursor: 'pointer', padding: 0, fontWeight: 600,
              }}
            >
              <IconChevronLeft size={14} />
              Back to {filterItemName} — Item detail
            </button>
          </div>
        )}

        {showPriceHistory ? (
          <div style={{ background: 'white', minHeight: '100vh' }}>
            <PriceHistoryPage
              onBack={() => setShowPriceHistory(false)}
              filterVendorId={filterHistoryVendorId}
              filterVendorName={filterHistoryVendorName}
              isMobile={true}
            />
          </div>
        ) : mobileShowDetail && selectedVendor ? (
          <div style={{ background: 'white', minHeight: '100vh' }}>
            <VendorDetailPanel
              vendor={selectedVendor}
              allVendorItems={allVendorItems}
              isMobile={true}
              onBack={() => setMobileShowDetail(false)}
              onVendorUpdated={load}
              setActive={setActive}
              setSelectedItemId={setSelectedItemId}
              filterItemId={filterItemId}
              filterItemName={filterItemName}
              canReview={canReview}
              needsReviewVendors={needsReviewVendors}
              onSelectVendor={handleVendorSelect}
              onShowPriceHistory={(vendorId) => {
                const v = vendors.find(v => v.id === vendorId);
                setFilterHistoryVendorId(vendorId);
                setFilterHistoryVendorName(v?.name || null);
                setShowPriceHistory(true);
                setMobileShowDetail(false);
              }}
            />
          </div>
        ) : (
          <div style={{ padding: '10px 12px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              border: '1px solid #E0E0E0', borderRadius: 8, padding: '8px 12px',
              marginBottom: 8, background: 'white',
            }}>
              <IconSearch size={14} color="#9A9A9A" />
              <input
                type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search vendors..."
                style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent' }}
              />
            </div>

            {/* #422, Session 78s — one action row across the top, taking the
                shape Colby asked for from Inventory item detail. It replaces
                Import/Export squeezed into the dark header bar below and the
                Add vendor button that used to sit under every card.
                The column count follows the BUTTON count, which is #364's
                lesson from that same screen: someone without import rights
                should see a clean three-up row, not a hole where a button
                would have been. */}
            {(() => {
              const actions = [
                ...(canAdd ? [{
                  key: 'add', label: 'Add', icon: true, primary: true,
                  onClick: () => setShowAddVendor(true),
                }] : []),
                ...(canImport ? [{
                  key: 'import', label: 'Import',
                  onClick: () => setShowImport(true),
                }] : []),
                ...(canExport ? [{
                  key: 'export', label: 'Export',
                  onClick: () => setShowExport(true),
                }] : []),
                {
                  key: 'history', label: 'History',
                  onClick: () => {
                    setFilterHistoryVendorId(null);
                    setFilterHistoryVendorName(null);
                    setShowPriceHistory(true);
                  },
                },
              ];
              return (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${actions.length}, 1fr)`,
                  gap: 6, marginBottom: 10,
                }}>
                  {actions.map(a => (
                    <button
                      key={a.key}
                      onClick={a.onClick}
                      style={{
                        padding: '10px 0',
                        background: a.primary ? '#1D9E75' : 'white',
                        color: a.primary ? 'white' : '#0F0F0F',
                        border: a.primary ? 'none' : '1px solid #E0E0E0',
                        borderRadius: 8, fontSize: 12, fontWeight: 700,
                        cursor: 'pointer', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: 4,
                      }}
                    >
                      {a.icon && <IconPlus size={13} />}
                      {a.label}
                    </button>
                  ))}
                </div>
              );
            })()}

            <div style={{
              background: '#0F0F0F', borderRadius: 8, padding: '8px 12px',
              marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'white' }}>Vendors</span>
              {/* #194 put Import and Export in this bar for #432's reason — a
                  control below forty vendor cards cannot be reached in two
                  taps. That reasoning is unchanged and is now served better by
                  the action row above, which is always visible and does not
                  compete with the count for width. This bar goes back to
                  labelling the list. */}
              <span style={{ fontSize: 11, color: '#9A9A9A' }}>
                {filteredVendors.length} {filterItemId ? 'for this item' : 'active'}
              </span>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#9A9A9A', fontSize: 13 }}>Loading...</div>
            ) : filteredVendors.map(v => (
              <VendorCard
                key={v.id} vendor={v} isSelected={false}
                onClick={() => handleVendorSelect(v)} isMobile={true}
                canContact={canContact}
              />
            ))}

          </div>
        )}

        <AddVendorSheet
          visible={showAddVendor}
          onClose={() => setShowAddVendor(false)}
          onSaved={handleVendorAdded}
          isMobile={true}
        />

        {showExport && (
          <VendorCatalogExportSheet
            isMobile={true}
            onClose={() => setShowExport(false)}
            statusFilter={statusFilter}
            search={search}
            profile={profile}
          />
        )}

        {showImport && (
          <MasterDataImportSheet
            isMobile={true}
            onClose={() => setShowImport(false)}
            onImported={load}
            auth={{ isAdmin, isManager, isExecutive, isPurchasing, profile }}
          />
        )}
      </div>
    );
  }

  // ── DESKTOP LAYOUT ─────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', overflow: 'hidden' }}>
      {filterItemId && filterItemName && (
        <div style={{ padding: '8px 20px', background: 'white', borderBottom: '1px solid #E8E8E8', flexShrink: 0 }}>
          <button
            onClick={() => { if (setSelectedItemId) setSelectedItemId(filterItemId); setActive('inv_detail'); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: 'none', border: 'none', color: '#1D9E75',
              fontSize: 12, cursor: 'pointer', padding: 0, fontWeight: 600,
            }}
          >
            <IconChevronLeft size={14} />
            Back to {filterItemName} — Item detail
          </button>
        </div>
      )}

      <StatBar
        vendors={vendors}
        vendorItems={allVendorItems}
        onPriceHistory={() => { setFilterHistoryVendorId(null); setFilterHistoryVendorName(null); setShowPriceHistory(true); }}
        canReview={canReview}
        onNeedsReviewClick={() => { if (needsReviewVendors.length > 0) handleVendorSelect(needsReviewVendors[0]); }}
        canExport={canExport}
        onExport={() => setShowExport(true)}
        canImport={canImport}
        onImport={() => setShowImport(true)}
      />

      {showExport && (
        <VendorCatalogExportSheet
          isMobile={false}
          onClose={() => setShowExport(false)}
          statusFilter={statusFilter}
          search={search}
          profile={profile}
        />
      )}

      {showImport && (
        <MasterDataImportSheet
          isMobile={false}
          onClose={() => setShowImport(false)}
          onImported={load}
          auth={{ isAdmin, isManager, isExecutive, isPurchasing, profile }}
        />
      )}

      {showPriceHistory ? (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <PriceHistoryPage
            onBack={() => setShowPriceHistory(false)}
            filterVendorId={filterHistoryVendorId}
            filterVendorName={filterHistoryVendorName}
            isMobile={false}
          />
        </div>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', flex: 1, overflow: 'hidden', position: 'relative' }}>
        <div style={{ borderRight: '1px solid #E8E8E8', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {ListPanel}
        </div>

        <div style={{ overflowY: 'auto', position: 'relative' }}>
          <VendorDetailPanel
            vendor={selectedVendor}
            allVendorItems={allVendorItems}
            isMobile={false}
            onBack={() => setSelectedVendorAndRef(null)}
            onVendorUpdated={load}
            setActive={setActive}
            setSelectedItemId={setSelectedItemId}
            filterItemId={filterItemId}
            filterItemName={filterItemName}
            canReview={canReview}
            needsReviewVendors={needsReviewVendors}
            onSelectVendor={handleVendorSelect}
            onShowPriceHistory={(vendorId) => {
              const v = vendors.find(v => v.id === vendorId);
              setFilterHistoryVendorId(vendorId);
              setFilterHistoryVendorName(v?.name || null);
              setShowPriceHistory(true);
            }}
          />
        </div>

        {showAddVendor && (
          <AddVendorSheet
            visible={showAddVendor}
            onClose={() => setShowAddVendor(false)}
            onSaved={handleVendorAdded}
            isMobile={false}
          />
        )}
      </div>
      )}
    </div>
  );
}